import { getRelationship, Reaction } from "~/src/api";
import { QueryClient, useQuery } from "@tanstack/vue-query";

const chapterIdRegex = /^[a-zA-Z0-9-]{8,36}$/;

const fetchUserChapterLike = async (chapterId: string) => {
  if (!process.client) return false;
  const nuxtApp = useNuxtApp();
  const authStore = nuxtApp.$auth();

  await authStore?.waitUntilRefreshIsComplete();
  const token = await authStore?.getToken();
  if (!token || !authStore?.userEntity) {
    return false;
  }

  const reactions = await Reaction.findReactions("chapter", {
    userIds: [authStore.userEntity.id],
    entityIds: [chapterId],
    limit: 1,
  });

  return !!reactions.data.find(
    (r) => getRelationship(r, "chapter").id === chapterId,
  )?.attributes.reactionType;
};

export const useGlobalChapterLike = (
  client: () => QueryClient,
  chapterId: string,
  fetchLike = true,
) => {
  const userLikedChapter = useState(`user-liked-chapter-${chapterId}`, () =>
    useGenericSyncedState(
      async () => {
        if (!chapterIdRegex.test(chapterId)) return false;
        return fetchUserChapterLike(chapterId);
      },
      async (value, signal) => {
        if (!chapterIdRegex.test(chapterId)) return value;
        const token = await getTokenOrPrompt();
        if (!token) return;

        if (value) {
          await Reaction.createReaction("chapter", chapterId, "like", token);
        } else {
          await Reaction.deleteReaction("chapter", chapterId, token);
        }

        return value;
      },
    ),
  );

  const { data: stats, isPending: pending } = useQuery(
    useChapterStats(chapterId),
    client(),
  );

  const chapterLikeCount = useState(`chapter-like-count-${chapterId}`, () => 0);

  const unwatch = watch(
    () => userLikedChapter.value.ready && stats.value,
    (v) => {
      if (v) {
        chapterLikeCount.value = sum(
          Object.values(stats.value!.attributes.reactions),
        );

        if (userLikedChapter.value.state) chapterLikeCount.value -= 1;

        unwatch();
      }
    },
  );

  return {
    state: computed(() => userLikedChapter.value.state),
    targetState: computed(() => userLikedChapter.value.targetState),
    ready: computed(() => userLikedChapter.value.ready && !pending.value),
    pending: computed(() => userLikedChapter.value.pending || pending.value),
    updateState: userLikedChapter.value.updateState,
    likeCount: computed(() =>
      Math.max(
        chapterLikeCount.value + (userLikedChapter.value.targetState ? 1 : 0),
        0,
      ),
    ),
  };
};
